import React, { useState } from "react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import toast from "react-hot-toast";

const MOVES = [
  { key: "rock", label: "Rock 🪨" },
  { key: "paper", label: "Paper 📄" },
  { key: "scissors", label: "Scissors ✂️" },
];

// Determines the winner (returns "user", "bot", or "draw")
function getWinner(userMove, botMove) {
  if (userMove === botMove) return "draw";
  if (
    (userMove === "rock" && botMove === "scissors") ||
    (userMove === "scissors" && botMove === "paper") ||
    (userMove === "paper" && botMove === "rock")
  ) {
    return "user";
  }
  return "bot";
}

export default function RPSGameVsComputer({ user, betAmount = 0.5, onBack }) {
  const [userMove, setUserMove] = useState(null);
  const [botMove, setBotMove] = useState(null);
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  function playSound(url) {
    const audio = new window.Audio(url);
    audio.volume = 0.55;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }
  // Handle move selection
  async function handleMove(move) {
    if (processing) return;
    setProcessing(true);
    playSound("/sounds/xo-click.mp3");

    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    let balance = userSnap.data().balance || 0;

    if (balance < betAmount) {
      toast.error("Insufficient balance!");
      setProcessing(false);
      return;
    }
    await updateDoc(userRef, { balance: +(balance - betAmount).toFixed(2) });

    const botMove = MOVES[Math.floor(Math.random() * 3)].key;
    setUserMove(move);
    setBotMove(botMove);

    const winner = getWinner(move, botMove);
    let msg = "";

    // Get old wins if any
    let wins = userSnap.data().wins || 0;
    let botWins = userSnap.data().botWins || 0;

    if (winner === "user") {
      const payout = +(betAmount * 2 * 0.9).toFixed(2);
      msg = `You win! +${payout} USDT credited.`;
      await updateDoc(userRef, {
        balance: +(balance - betAmount + payout).toFixed(2),
        wins: wins + 1, // 👈 Player gets 1 win
      });
      toast.success(msg);
      playSound("/sounds/win.mp3");
    } else if (winner === "bot") {
      msg = "You lost! Better luck next time.";
      // Add 2 wins to bot
      await updateDoc(userRef, {
        botWins: botWins + 2, // 👈 Bot gets 2 wins
      });
      toast.error(msg);
      playSound("/sounds/lose.mp3");
    } else {
      await updateDoc(userRef, {
        balance: +balance.toFixed(2),
      });
      msg = "Draw! Your bet has been refunded.";
      toast("Draw!");
      playSound("/sounds/draw.mp3");
    }
    setResult(winner);
    setProcessing(false);
  }

  return (
    <div className="w-full max-w-md mx-auto mt-8 bg-slate-900 border-2 border-lime-400 rounded-2xl shadow-xl p-6 flex flex-col items-center">
      <div className="text-lime-400 font-extrabold text-2xl mb-2">
        Rock Paper Scissors
      </div>
      <div className="mb-4 text-slate-300">
        Bet Amount:{" "}
        <span className="text-lime-300 font-bold">{betAmount} USDT</span>
      </div>
      <div className="flex gap-4 mb-8">
        {MOVES.map((m) => (
          <button
            key={m.key}
            className={`px-5 py-3 text-lg font-bold rounded-2xl border-2 border-lime-500 bg-slate-800 text-lime-300 hover:bg-lime-400 hover:text-black transition 
              ${userMove === m.key ? "bg-lime-300 text-black scale-110" : ""}
            `}
            onClick={() => handleMove(m.key)}
            disabled={!!userMove || processing}
          >
            {m.label}
          </button>
        ))}
      </div>
      {userMove && (
        <div className="w-full flex flex-col items-center mt-6">
          <div className="flex gap-12 items-center">
            <div className="flex flex-col items-center">
              <div className="text-slate-400 mb-1">You</div>
              <div className="text-3xl">
                {MOVES.find((m) => m.key === userMove)?.label}
              </div>
            </div>
            <div className="text-lime-500 font-black text-xl">vs</div>
            <div className="flex flex-col items-center">
              <div className="text-slate-400 mb-1">Bot</div>
              <div className="text-3xl">
                {MOVES.find((m) => m.key === botMove)?.label}
              </div>
            </div>
          </div>
          <div className="mt-7 text-xl font-bold">
            {result === "user" && (
              <span className="text-lime-400">🎉 You win!</span>
            )}
            {result === "bot" && (
              <span className="text-pink-400">😭 You lost!</span>
            )}
            {result === "draw" && (
              <span className="text-yellow-300">🤝 Draw!</span>
            )}
          </div>
          <button
            className="mt-6 px-8 py-3 rounded-xl bg-slate-800 text-lime-300 font-bold hover:bg-lime-400 hover:text-black shadow transition"
            onClick={() => {
              setUserMove(null);
              setBotMove(null);
              setResult(null);
            }}
          >
            Play Again
          </button>
          <button
            className="mt-2 text-xs text-slate-400 underline"
            onClick={onBack}
          >
            ← Back to Lobby
          </button>
        </div>
      )}
    </div>
  );
}
